import { useState, useCallback, useEffect } from 'react';
import { useJourneyEdit, useJourneyEditActions } from '../../../contexts/JourneyEditContext';
import type { Change } from '../../../contexts/JourneyEditContext';
import { useToast } from '../../../hooks/useToast';
import {
  submitJourneyReview,
  approveCreateJourney,
  approveUpdateJourney,
  fetchJourneyDetail,
} from '../../journeys/api';
import {
  createAddStopChange,
  createReplaceStopChange,
  createRemoveStopChange,
  createUpdateLegTransportChange,
  transformChangesToAPI,
} from '../services/changeBuilder';

/**
 * Main hook for journey editing functionality.
 * 
 * This hook provides a clean interface for journey editing operations,
 * encapsulating all business logic related to:
 * - Adding, removing, and replacing stops
 * - Updating transport modes
 * - Submitting changes for review
 * - Handling approvals (create new, update existing)
 * - Managing edit state (view/edit mode, awaiting approval)
 * 
 * This hook serves as the primary interface between the UI and the
 * journey editing system, providing a stable API for components.
 */
export function useJourneyEditor() {
  const { state } = useJourneyEdit();
  const actions = useJourneyEditActions();
  const { showToast } = useToast();
  
  // Local UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notesMessage, setNotesMessage] = useState('');
  const [submitController, setSubmitController] = useState<AbortController | null>(null);
  
  /**
   * Initiates adding a new stop after the specified position.
   * 
   * @param afterOrder - The order position after which to add the stop
   * @param method - The method used to add the stop
   * @param city - The city name (for manual method)
   * @param country - The country name (for manual method)
   * @param instructions - AI instructions (for ai_assisted method)
   */
  const addStop = useCallback((
    afterOrder: number,
    method: 'manual' | 'ai_assisted',
    city?: string,
    country?: string,
    instructions?: string
  ) => {
    if (state.awaitingApproval) return;
    
    // Switch to edit mode if not already
    if (state.mode === 'view') {
      actions.enterEditMode();
    }
    
    const sequence = state.pendingChanges.length + 1;
    const change = createAddStopChange(
      afterOrder,
      sequence,
      method,
      city,
      country,
      instructions
    );
    
    actions.addChange(change);
  }, [state.awaitingApproval, state.mode, state.pendingChanges.length, actions]);
  
  /**
   * Replaces an existing stop with a new location.
   * 
   * @param stopId - The UUID of the stop to replace
   * @param method - The method used to replace the stop
   * @param city - The new city name (for manual method)
   * @param country - The new country name (for manual method)
   * @param instructions - AI instructions (for ai_assisted method)
   */
  const replaceStop = useCallback((
    stopId: string,
    method: 'manual' | 'ai_assisted',
    city?: string,
    country?: string,
    instructions?: string
  ) => {
    if (state.awaitingApproval) return;
    
    // Switch to edit mode if not already
    if (state.mode === 'view') {
      actions.enterEditMode();
    }
    
    const sequence = state.pendingChanges.length + 1;
    const change = createReplaceStopChange(
      stopId,
      sequence,
      method,
      city,
      country,
      instructions
    );
    
    actions.addChange(change);
  }, [state.awaitingApproval, state.mode, state.pendingChanges.length, actions]);
  
  /**
   * Removes a stop from the journey.
   * 
   * @param stopId - The UUID of the stop to remove
   */
  const removeStop = useCallback((stopId: string) => {
    if (state.awaitingApproval) return;
    
    // Find the stop to get its order
    const stopToRemove = state.currentJourney?.stops?.find(
      (s: any) => s.id === stopId
    );
    const stopOrder = stopToRemove?.order ?? 0;
    
    // Switch to edit mode if not already
    if (state.mode === 'view') {
      actions.enterEditMode();
    }
    
    const sequence = state.pendingChanges.length + 1;
    const change = createRemoveStopChange(stopId, stopOrder, sequence);
    
    actions.addChange(change);
  }, [state.awaitingApproval, state.mode, state.pendingChanges.length, state.currentJourney, actions]);
  
  /**
   * Updates the transport mode for a leg.
   * 
   * @param legId - The UUID of the leg to update
   * @param transportMode - The new transport mode
   */
  const updateLegTransport = useCallback((
    legId: string,
    transportMode: string
  ) => {
    if (state.awaitingApproval) return;
    
    // Switch to edit mode if not already
    if (state.mode === 'view') {
      actions.enterEditMode();
    }
    
    const sequence = state.pendingChanges.length + 1;
    const change = createUpdateLegTransportChange(legId, transportMode, sequence);
    
    actions.addChange(change);
  }, [state.awaitingApproval, state.mode, state.pendingChanges.length, actions]);
  
  /**
   * Submits all pending changes for AI review.
   * 
   * This function handles:
   * 1. Validation of pending changes
   * 2. Auto-save for unsaved journeys
   * 3. Transformation of changes to API format
   * 4. Submission to backend
   * 5. Handling of response and notes
   */
  const submitChanges = useCallback(async () => {
    const controller = new AbortController();
    setSubmitController(controller);
    if (state.pendingChanges.length === 0) {
      showToast({
        title: 'No changes',
        message: 'Please make some changes before submitting.',
        type: 'info',
      });
      return;
    }
    
    // Check if this is an unsaved journey (no backend ID yet)
    let journeyId = (state.originalJourney as any)?.id;
    
    if (!journeyId) {
      // Auto-save the original journey first
      console.log('[useJourneyEditor] Auto-saving unsaved journey...');
      actions.startSubmitting();
      setIsSubmitting(true);
      
      try {
        const saveResponse = await approveCreateJourney<any>(state.originalJourney as any);
        
        if (!saveResponse.success || !(saveResponse as any).journey?.id) {
          const errorMessage = saveResponse.error?.message || 'Failed to save journey.';
          showToast({
            title: 'Save Failed',
            message: errorMessage,
            type: 'error',
          });
          actions.finishSubmitting();
          setIsSubmitting(false);
          setSubmitController(null);
          return;
        }
        
        const savedJourneyResponse = (saveResponse as any).journey;
        
        // Fetch full journey details if needed
        if (!savedJourneyResponse.stops || !Array.isArray(savedJourneyResponse.stops)) {
          const fullJourneyDetail = await fetchJourneyDetail<any>(savedJourneyResponse.id);
          journeyId = fullJourneyDetail.id;
          actions.updateOriginalJourneyId(journeyId, fullJourneyDetail);
        } else {
          journeyId = savedJourneyResponse.id;
          actions.updateOriginalJourneyId(journeyId, savedJourneyResponse);
        }
        
        showToast({
          title: 'Journey saved',
          message: 'Processing your changes...',
          type: 'success',
        });
      } catch (error) {
        console.error('[useJourneyEditor] Error auto-saving journey:', error);
        showToast({
          title: 'Error',
          message: 'Failed to save journey. Please try again.',
          type: 'error',
        });
        actions.finishSubmitting();
        setIsSubmitting(false);
        return;
      }
    } else {
      actions.startSubmitting();
      setIsSubmitting(true);
    }
    
    // At this point, journeyId is guaranteed to exist
    if (!journeyId) {
      showToast({
        title: 'Error',
        message: 'Journey ID not found. Please refresh the page.',
        type: 'error',
      });
      actions.finishSubmitting();
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Transform changes to API format
      const apiChanges = transformChangesToAPI(state.pendingChanges as Change[]);
      
      console.log('[useJourneyEditor] Submitting changes:', {
        journeyId,
        changes: apiChanges,
        count: apiChanges.length,
      });
      
      const response = await submitJourneyReview(journeyId, apiChanges, controller.signal);
      
      if (response.success && response.journey) {
        // Check if any stops were added or changed
        const hasStopChanges = state.pendingChanges.some((change: any) =>
          change.type === 'add_stop' || change.type === 'replace_stop'
        );

        // Update journey with processed data and enter approval state
        actions.setAwaitingApproval(response.journey);

        showToast({
          title: 'Changes processed',
          message: 'Review the changes and approve to save them.',
          type: 'success',
        });

        // Show notes dialog if present and stops were changed
        if (response.notes && response.notes.trim() && hasStopChanges) {
          setNotesMessage(response.notes);
          setShowNotesDialog(true);
        }
      } else {
        // Check if this is an abort error (user cancelled the request on purpose)
        const isAbortError = response.error?.code === 'ABORTED' ||
                            response.error?.message?.includes('aborted') ||
                            response.error?.message?.includes('cancelled');

        if (!isAbortError) {
          const errorMessage = response.error?.message || 'Failed to process changes.';
          showToast({
            title: 'Error',
            message: errorMessage,
            type: 'error',
          });
        }
        actions.finishSubmitting();
        setIsSubmitting(false);
      }
    } catch (error: any) {
      // Check if this is an abort error (user cancelled the request on purpose)
      const isAbortError = error?.name === 'AbortError' ||
                          error?.message?.includes('aborted') ||
                          error?.message?.includes('cancelled') ||
                          error?.code === 'ERR_CANCELED';

      if (!isAbortError) {
        console.error('[useJourneyEditor] Error submitting changes:', error);
        showToast({
          title: 'Error',
          message: 'An unexpected error occurred. Please try again.',
          type: 'error',
        });
      }
      actions.finishSubmitting();
      setIsSubmitting(false);
    }
  }, [
    state.pendingChanges,
    state.originalJourney,
    actions,
    showToast,
  ]);
  
  /**
   * Approves changes by updating the existing journey.
   * Only available when a journey has an existing ID.
   */
  const approveUpdate = useCallback(async () => {
    if (!state.originalJourney?.id || !state.processedJourney) return;
    
    try {
      setIsSubmitting(true);
      actions.startSubmitting();
      
      const res = await approveUpdateJourney<any>(
        state.originalJourney.id,
        state.processedJourney
      );
      
      if (res.success && (res as any).journey?.id) {
        const updated = await fetchJourneyDetail<any>((res as any).journey.id);
        actions.loadJourney(updated as any);
        actions.clearApprovalState();
        actions.exitEditMode();
        
        showToast({
          title: 'Journey updated',
          message: 'Your journey has been updated.',
          type: 'success',
        });
        
        return updated;
      } else {
        showToast({
          title: 'Update failed',
          message: res.error?.message || 'Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('[useJourneyEditor] Error approving update:', error);
      showToast({
        title: 'Error',
        message: 'An unexpected error occurred.',
        type: 'error',
      });
    } finally {
      actions.finishSubmitting();
      setIsSubmitting(false);
    }
  }, [state.originalJourney, state.processedJourney, actions, showToast]);
  
  /**
   * Approves changes by creating a new journey (duplicate).
   * Available when a journey has an existing ID.
   */
  const approveCreateNew = useCallback(async () => {
    if (!state.processedJourney) return;
    
    try {
      setIsSubmitting(true);
      actions.startSubmitting();
      
      const res = await approveCreateJourney<any>(
        state.processedJourney,
        state.originalJourney?.id
      );
      
      if (res.success && (res as any).journey?.id) {
        const saved = await fetchJourneyDetail<any>((res as any).journey.id);
        actions.clearApprovalState();
        actions.exitEditMode();
        
        showToast({
          title: 'New journey created',
          message: 'Your journey has been saved.',
          type: 'success',
        });
        
        return saved;
      } else {
        showToast({
          title: 'Save failed',
          message: res.error?.message || 'Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('[useJourneyEditor] Error creating new journey:', error);
      showToast({
        title: 'Error',
        message: 'An unexpected error occurred.',
        type: 'error',
      });
    } finally {
      actions.finishSubmitting();
      setIsSubmitting(false);
    }
  }, [state.processedJourney, state.originalJourney, actions, showToast]);
  
  /**
   * Approves changes by saving an unsaved journey.
   * Used when the journey doesn't have an existing ID.
   */
  const approveCreateUnsaved = useCallback(async () => {
    if (!state.processedJourney) return;
    
    try {
      setIsSubmitting(true);
      actions.startSubmitting();
      
      const res = await approveCreateJourney<any>(state.processedJourney);
      
      if (res.success && (res as any).journey?.id) {
        const saved = await fetchJourneyDetail<any>((res as any).journey.id);
        actions.clearApprovalState();
        actions.exitEditMode();
        
        showToast({
          title: 'Journey saved',
          message: 'Your journey has been saved.',
          type: 'success',
        });
        
        return saved;
      } else {
        showToast({
          title: 'Save failed',
          message: res.error?.message || 'Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('[useJourneyEditor] Error saving journey:', error);
      showToast({
        title: 'Error',
        message: 'An unexpected error occurred.',
        type: 'error',
      });
    } finally {
      actions.finishSubmitting();
      setIsSubmitting(false);
    }
  }, [state.processedJourney, actions, showToast]);
  
  /**
   * Discards all pending changes and exits edit mode.
   */
  const discardChanges = useCallback(() => {
    actions.discardChanges();
  }, [actions]);
  
  /**
   * Enters edit mode for the journey.
   */
  const enterEditMode = useCallback(() => {
    actions.enterEditMode();
  }, [actions]);
  
  /**
   * Exits edit mode without saving changes.
   */
  const exitEditMode = useCallback(() => {
    actions.exitEditMode();
  }, [actions]);
  
  /**
   * Cancels the current submit operation.
   */
  const cancelSubmitChanges = useCallback(() => {
    if (submitController) {
      submitController.abort();
      setSubmitController(null);
      actions.finishSubmitting();
      setIsSubmitting(false);
      showToast({
        title: 'Submission cancelled',
        message: 'You can edit your changes and try again.',
        type: 'info',
        duration: 4000
      });
    }
  }, [submitController, actions, showToast]);
  
  /**
   * Closes the notes dialog.
   */
  const closeNotesDialog = useCallback(() => {
    setShowNotesDialog(false);
    setNotesMessage('');
  }, []);
  
  // Sync isSubmitting with context state
  useEffect(() => {
    setIsSubmitting(state.isSubmitting);
  }, [state.isSubmitting]);
  
  return {
    // State
    mode: state.mode,
    awaitingApproval: state.awaitingApproval,
    isSubmitting,
    pendingChanges: state.pendingChanges,
    currentJourney: state.currentJourney,
    originalJourney: state.originalJourney,
    processedJourney: state.processedJourney,
    showNotesDialog,
    notesMessage,
    
    // Actions
    addStop,
    replaceStop,
    removeStop,
    updateLegTransport,
    submitChanges,
    cancelSubmitChanges,
    approveUpdate,
    approveCreateNew,
    approveCreateUnsaved,
    discardChanges,
    enterEditMode,
    exitEditMode,
    closeNotesDialog,
  };
}

